export type User = {
  id: string;
  name: string;
  email: string;
  currency: string;
  avatar?: string;
  color?: number;
  exchangeRates?: Record<string, number>;
  role?: "user" | "admin";
};

export const SUPPORTED_CURRENCIES = ["AWG", "EUR", "USD"] as const;
export function currencyOptions(current?: string): string[] {
  return Array.from(
    new Set(
      [current?.toUpperCase(), ...SUPPORTED_CURRENCIES].filter(
        Boolean,
      ) as string[],
    ),
  );
}

export type Person = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  notes?: string;
  color: number;
};

export type CreatePersonInput = {
  displayName: string;
  email?: string;
  phone?: string;
  avatar?: string;
  notes?: string;
};

export type Group = {
  id: string;
  name: string;
  description: string;
  memberIds: string[];
};

export type Expense = {
  notes?: string;
  id: string;
  description: string;
  amountCents: number;
  currency: string;
  groupId: string;
  paidById: string;
  payerAmounts?: Record<string, number>;
  participantIds: string[];
  participantAmounts?: Record<string, number>;
  occurredAt?: string;
  occurredTimeSet?: boolean;
  createdAt: string;
};

export type Settlement = {
  description?: string;
  id: string;
  personId: string;
  amountCents: number;
  currency: string;
  direction: "paid" | "received";
  method?: string;
  paidAt?: string;
  paidTimeSet?: boolean;
  createdAt: string;
};

export type CurrencyExchange = {
  id: string;
  personId: string;
  fromAmountCents: number;
  fromCurrency: string;
  toAmountCents: number;
  toCurrency: string;
  rateBps: number;
  occurredAt?: string;
  createdAt: string;
};

export type Balance = {
  personId: string;
  amountCents: number;
  currency: string;
};

export type SettlementSuggestion = {
  fromPersonId: string;
  toPersonId: string;
  amountCents: number;
  currency: string;
};

export type SettlementRequestStatus = "pending" | "confirmed" | "rejected";

export type GuestSettlementRequest = {
  id: string;
  guestShareLinkId?: string;
  personShareLinkId?: string;
  expenseId?: string;
  personId: string;
  ownerPersonId: string;
  amountCents: number;
  currency: string;
  note?: string;
  status: SettlementRequestStatus;
  rejectionReason?: string;
  settlementId?: string;
  requestedAt: string;
  reviewedAt?: string;
  reviewedById?: string;
};

export type GuestShareLink = {
  id: string;
  status: "active" | "settled" | "expired" | "revoked" | "closed";
  expiresAt?: string;
  createdAt: string;
};

export type GuestShareParticipant = {
  personId: string;
  currency: string;
  originalAmountCents: number;
  confirmedAmountCents: number;
  pendingAmountCents: number;
  remainingAmountCents: number;
  requestableAmountCents: number;
  activeLink?: GuestShareLink;
  requests: GuestSettlementRequest[];
};

export type ExpenseGuestShareOverview = {
  expenseId: string;
  participants: GuestShareParticipant[];
};

export type CreatedGuestShareLink = {
  linkId: string;
  token: string;
  expiresAt?: string;
};

export type GuestSettlementRequestPublic = {
  amountCents: number;
  currency: string;
  note?: string;
  status: SettlementRequestStatus;
  rejectionReason?: string;
  requestedAt: string;
  reviewedAt?: string;
};

export type GuestShareView = {
  expenseDescription: string;
  expenseOccurredAt: string;
  guestName: string;
  ownerName: string;
  currency: string;
  originalAmountCents: number;
  confirmedAmountCents: number;
  pendingAmountCents: number;
  remainingAmountCents: number;
  requestableAmountCents: number;
  status: "active" | "settled" | "expired" | "revoked" | "closed";
  expiresAt?: string;
  requests: GuestSettlementRequestPublic[];
};

export type PersonShareLink = {
	loanId?: string;
	viewedAt?: string;
	acknowledgedAt?: string;
  id: string;
  status: "active" | "expired" | "revoked";
  showBalances: boolean;
  showExpenses: boolean;
  showPayments: boolean;
  showLoans: boolean;
  expiresAt?: string;
  createdAt: string;
};

export type PersonShareOverview = {
	loanClaims?: LoanPaymentClaim[];
  personId: string;
  activeLink?: PersonShareLink;
  requests: GuestSettlementRequest[];
};

export type CreatedPersonShareLink = {
  linkId: string;
  token: string;
  expiresAt?: string;
};

export type PersonShareBalance = {
  currency: string;
  amountCents: number;
  inProgressCents: number;
  remainingAfterPendingCents: number;
  requestableAmountCents: number;
};

export type PersonShareExpense = {
  id: string;
  description: string;
  currency: string;
  balanceImpactCents: number;
  occurredAt: string;
};

export type PersonSharePayment = {
  id: string;
  currency: string;
  amountCents: number;
  direction: "paid_owner" | "owner_paid";
  method?: string;
  paidAt: string;
};

export type PersonShareLoan = {
	claims?: LoanPaymentClaim[];
	balance?: LoanBalance;
	monthlyInterestBps?: number;
	monthlyInterestCents?: number;
	compound?: boolean;
	issuedAt?: string;
	penalty?: LoanPenaltyTerms;
	expenseSources?: LoanExpenseSource[];
	payments?: LoanPayment[];
	accruals?: LoanAccrual[];
  id: string;
  currency: string;
  direction: "person_borrowed" | "owner_borrowed";
  outstandingCents: number;
  dueAt?: string;
  status: string;
};

export type PersonShareView = {
	breakdown?: PersonDebtBalance[];
	acknowledgedAt?: string;
  personName: string;
  ownerName: string;
  status: "active" | "expired" | "revoked";
  expiresAt?: string;
  updatedAt: string;
  showBalances: boolean;
  showExpenses: boolean;
  showPayments: boolean;
  showLoans: boolean;
  balances?: PersonShareBalance[];
  expenses?: PersonShareExpense[];
  payments?: PersonSharePayment[];
  loans?: PersonShareLoan[];
  requests?: GuestSettlementRequestPublic[];
};

export type ImportPreview = {
  id: string;
  source: string;
  status: string;
  expenses?: Array<{ id: string; description: string; amountCents: number; currency: string }>;
  settlements?: Array<{ id: string; amountCents: number; currency: string }>;
  loans?: Array<{ id: string; principalCents: number; currency: string }>;
  error?: string;
  createdAt: string;
  updatedAt: string;
};

export type Overview = {
	combinedBalances?: Balance[];
	expenseTransfers?: Array<{ loanId: string; personId: string; amountCents: number; currency: string; createdAt: string }>;
  currentUser: User;
  people: Person[];
  groups: Group[];
  expenses: Expense[];
  settlements: Settlement[];
  exchanges: CurrencyExchange[];
  balances: Balance[];
};

export type Loan = {
	installments?: LoanInstallment[];
	monthlyInterestCents?: number;
	interestStartsAt?: string;
	penalty?: LoanPenaltyTerms;
	expenseSources?: LoanExpenseSource[];
	termsHistory?: Array<{ effectiveAt: string; monthlyInterestBps: number; monthlyInterestCents: number; compound: boolean; penalty: LoanPenaltyTerms; dueAt?: string }>;
  id: string;
  lenderPersonId: string;
  borrowerPersonId: string;
  principalCents: number;
  currency: string;
  fee: { type: "fixed" | "percentage"; amountCents?: number; rateBps?: number };
  monthlyInterestBps: number;
  accrual: "daily";
  compound: boolean;
  issuedAt: string;
  dueAt?: string;
  status: "DRAFT" | "ACTIVE" | "OVERDUE" | "PAID" | "CANCELLED";
};

export type LoanBalance = {
	penaltyOutstandingCents?: number;
  loanId: string;
  currency: string;
  principalOutstandingCents: number;
  feeOutstandingCents: number;
  interestOutstandingCents: number;
  outstandingCents: number;
};

export type LoanPayment = {
	penaltyCents?: number;
  id: string;
  loanId: string;
  amountCents: number;
  interestCents: number;
  feeCents: number;
  principalCents: number;
  paidAt: string;
  method?: string;
  notes?: string;
};

export type CreateLoanInput = {
	installments?: LoanInstallment[];
	monthlyInterestCents?: number;
	interestStartsAt?: string;
	penalty?: LoanPenaltyTerms;
  lenderPersonId: string;
  borrowerPersonId: string;
  principalCents: number;
  currency: string;
  issuedAt?: string;
  feeType: "fixed" | "percentage";
  feeAmountCents?: number;
  feeRateBps?: number;
  monthlyInterestBps: number;
  accrual?: "daily";
  compound?: boolean;
  dueAt?: string;
};

export type CreateLoanPaymentInput = {
  amountCents: number;
  method?: string;
  notes?: string;
};

export type LoanPenaltyTerms = { type?: 'none' | 'fixed' | 'percentage'; amountCents?: number; rateBps?: number; frequency?: 'once' | 'monthly'; graceDays: number; capCents?: number };
export type LoanExpenseSource = { expenseId: string; amountCents: number; description?: string };
export type LoanAccrual = { id: string; loanId: string; accrualDate: string; interestCents: number; penaltyCents?: number };
export type PersonDebtBalance = { currency: string; expenseCents: number; principalCents: number; interestCents: number; feeCents: number; penaltyCents: number; theyOweCents: number; youOweCents: number; netCents: number };
export type ConvertibleExpense = { expenseId: string; description: string; currency: string; outstandingCents: number; transferredCents: number; loanIds: string[]; occurredAt: string };
export type PersonDebt = { asOf: string; balances: PersonDebtBalance[]; expenses: ConvertibleExpense[]; loans: Array<{ loan: Loan; balance: LoanBalance; payments: LoanPayment[]; accruals: LoanAccrual[] }> };
export type PersonPaymentAllocation = { loanId?: string; amountCents: number };
export type LoanInstallment = { dueAt: string; principalCents: number };
export type LoanForecast = { asOf: string; payoffAt: string; currentCents: number; payoffCents: number; interestCents: number; penaltyCents: number; nextChargeAt?: string; nextChargeCents: number; installments: Array<LoanInstallment & { paidCents: number; remainingCents: number; overdueCents: number }> };
export type LoanPaymentClaim = { id: string; loanId: string; currency: string; amountCents: number; note?: string; status: 'pending' | 'confirmed' | 'rejected'; requestedAt: string; reviewedAt?: string; paymentId?: string };

export type AuthResponse = {
  token: string;
  user: {
    id: string;
    personId: string;
    email: string;
    defaultCurrency: string;
    role?: "user" | "admin";
    status: string;
  };
};

export type AdminAccount = {
  id: string;
  personId: string;
  displayName: string;
  email: string;
  defaultCurrency: string;
  role: "user" | "admin";
  status: string;
  passkeyCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CurrentAccount = {
  id: string;
  personId: string;
  email: string;
  defaultCurrency: string;
  exchangeRates?: Record<string, number>;
  role?: "user" | "admin";
  status: string;
};

export type PasskeyOptionsResponse = {
  sessionId: string;
  options: Record<string, unknown>;
};

export type PasskeyCredentialJSON = {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject?: string;
    authenticatorData?: string;
    signature?: string;
    userHandle?: string | null;
  };
};

export const AUTH_TOKEN_KEY = "settleup.token";
export const AUTH_ACTIVITY_KEY = "settleup.lastActivityAt";

export type DatabaseSyncPhase =
  | "PREPARING"
  | "DOWNLOADING"
  | "VERIFYING"
  | "APPLYING"
  | "COMPLETED"
  | "FAILED";

export type DatabaseSyncJob = {
  id: string;
  status: DatabaseSyncPhase;
  phase: DatabaseSyncPhase;
  percent: number;
  sourceEnvironment: "production";
  targetEnvironment: "development" | "staging";
  downloadedBytes: number;
  totalBytes?: number | null;
  snapshotGeneratedAt?: string;
  startedAt: string;
  completedAt?: string;
  checksum?: string;
  error?: string;
  message: string;
};

export type DatabaseSyncState = {
  environment: string;
  canSyncFromProduction: boolean;
  activeJob?: DatabaseSyncJob | null;
  lastJob?: DatabaseSyncJob | null;
};

export type CreateGroupInput = {
  name: string;
  description: string;
  memberIds: string[];
};

export type CreateExpenseInput = {
  notes?: string;
  description: string;
  amountCents: number;
  currency: string;
  occurredAt?: string;
  occurredTimeSet?: boolean;
  groupId?: string;
  paidById: string;
  participantIds: string[];
  splitType?: "equal" | "exact" | "percentage" | "shares" | "itemized";
  splits?: Array<{
    personId: string;
    amountCents?: number;
    percentage?: number;
    shares?: number;
  }>;
};

export type UpdateExpenseInput = {
  notes?: string;
  description: string;
  amountCents: number;
  currency: string;
  occurredAt?: string;
  occurredTimeSet?: boolean;
};

export type CreateSettlementInput = {
  description?: string;
  personId: string;
  amountCents: number;
  currency: string;
  direction: "paid" | "received";
  paidAt?: string;
  paidTimeSet?: boolean;
};

export type CreateCurrencyExchangeInput = {
  personId: string;
  fromAmountCents: number;
  fromCurrency: string;
  toAmountCents: number;
  toCurrency: string;
  rateBps: number;
};

export type ProfileSettingsInput = {
  email: string;
  currency: string;
  avatar: string;
  exchangeRates: Record<string, number>;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

type APIError = {
  error?: string | { message?: string };
};

export function getStoredAuthToken() {
  return typeof window !== "undefined"
    ? window.localStorage.getItem(AUTH_TOKEN_KEY)
    : null;
}

export function storeAuthSession(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  window.localStorage.setItem(AUTH_ACTIVITY_KEY, String(Date.now()));
}

export function storeAuthToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthSession() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_ACTIVITY_KEY);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredAuthToken();
  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(!isFormData ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  const payload = (await response.json().catch(() => ({}))) as T & APIError;
  if (!response.ok) {
    if (
      response.status === 401 &&
      typeof window !== "undefined" &&
      path.startsWith("/api/v1")
    ) {
      window.dispatchEvent(new Event("settleup:auth-expired"));
    }
    const message =
      typeof payload.error === "string"
        ? payload.error
        : payload.error?.message;
    throw new Error(message ?? "Something went wrong. Please try again.");
  }
  return payload;
}

export function normalizeOverview(overview: Overview): Overview {
  return {
    ...overview,
    people: overview.people ?? [],
    groups: overview.groups ?? [],
    expenses: overview.expenses ?? [],
    settlements: overview.settlements ?? [],
    exchanges: overview.exchanges ?? [],
    balances: overview.balances ?? [],
  };
}

async function requestOverview(path: string, init?: RequestInit): Promise<Overview> {
  return normalizeOverview(await request<Overview>(path, init));
}

export const ledgerAPI = {
  getOverview: () => requestOverview("/api/overview"),
  createGroup: (input: CreateGroupInput) =>
    requestOverview("/api/groups", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createExpense: (input: CreateExpenseInput) =>
    requestOverview("/api/expenses", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createSettlement: (input: CreateSettlementInput) =>
    requestOverview("/api/settlements", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createCurrencyExchange: (input: CreateCurrencyExchangeInput) =>
    requestOverview("/api/exchanges", {
      method: "POST",
      body: JSON.stringify(input),
    }),
};

export const v1API = {
  login: (email: string, password: string) =>
    request<AuthResponse>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  passkeyLoginOptions: () =>
    request<PasskeyOptionsResponse>('/api/v1/auth/passkeys/login/options', {
      method: 'POST',
      body: '{}',
    }),
  passkeyLoginVerify: (sessionId: string, credential: PasskeyCredentialJSON) =>
    request<AuthResponse>('/api/v1/auth/passkeys/login/verify', {
      method: 'POST',
      body: JSON.stringify({ sessionId, credential }),
    }),
  passkeyRegistrationOptions: () =>
    request<PasskeyOptionsResponse>('/api/v1/auth/passkeys/registration/options', {
      method: 'POST',
      body: '{}',
    }),
  passkeyRegistrationVerify: (sessionId: string, credential: PasskeyCredentialJSON) =>
    request<void>('/api/v1/auth/passkeys/registration/verify', {
      method: 'POST',
      body: JSON.stringify({ sessionId, credential }),
    }),
  me: () => request<CurrentAccount>('/api/v1/me'),
  listAdminAccounts: () => request<AdminAccount[]>('/api/v1/admin/accounts'),
  resetAdminAccountPassword: (accountID: string, newPassword: string) =>
    request<void>(`/api/v1/admin/accounts/${encodeURIComponent(accountID)}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),
  revokeAdminAccountSessions: (accountID: string) =>
    request<void>(`/api/v1/admin/accounts/${encodeURIComponent(accountID)}/revoke-sessions`, {
      method: 'POST',
      body: '{}',
    }),
  register: (
    email: string,
    password: string,
    displayName: string,
    defaultCurrency = "AWG",
  ) =>
    request<AuthResponse>("/api/v1/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName, defaultCurrency }),
    }),
  refresh: () =>
    request<AuthResponse>("/api/v1/auth/refresh", {
      method: "POST",
      body: "{}",
    }),
  getDatabaseSyncState: () =>
    request<DatabaseSyncState>("/api/v1/system/database-sync"),
  startDatabaseSync: () =>
    request<DatabaseSyncJob>("/api/v1/system/database-sync", {
      method: "POST",
      body: "{}",
    }),
  getDatabaseSync: (syncID: string) =>
    request<DatabaseSyncJob>(
      `/api/v1/system/database-sync/${encodeURIComponent(syncID)}`,
    ),
  createPerson: (input: CreatePersonInput) =>
    request("/api/v1/people", {
      method: "POST",
      headers: { "Idempotency-Key": `person-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  updatePerson: (personID: string, input: CreatePersonInput) =>
    request(`/api/v1/people/${encodeURIComponent(personID)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateExpense: (expenseID: string, input: UpdateExpenseInput) =>
    request<Expense>(`/api/v1/expenses/${encodeURIComponent(expenseID)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  reverseExpense: (expenseID: string) =>
    request<void>(`/api/v1/expenses/${encodeURIComponent(expenseID)}`, {
      method: "DELETE",
    }),
  getExpenseGuestShares: (expenseID: string) =>
    request<ExpenseGuestShareOverview>(`/api/v1/expenses/${encodeURIComponent(expenseID)}/guest-share-links`),
  createExpenseGuestShare: (expenseID: string, personID: string, expiresInDays = 30) =>
    request<CreatedGuestShareLink>(`/api/v1/expenses/${encodeURIComponent(expenseID)}/guest-share-links`, {
      method: "POST",
      body: JSON.stringify({ personId: personID, expiresInDays }),
    }),
  revokeGuestShareLink: (linkID: string) =>
    request<{ revoked: boolean }>(`/api/v1/guest-share-links/${encodeURIComponent(linkID)}`, {
      method: "DELETE",
    }),
  getPersonShare: (personID: string) =>
    request<PersonShareOverview>(`/api/v1/people/${encodeURIComponent(personID)}/share`),
  createPersonShare: (personID: string, input: { showBalances: boolean; showExpenses: boolean; showPayments: boolean; showLoans: boolean; expiresInDays?: number; loanId?: string }) =>
    request<CreatedPersonShareLink>(`/api/v1/people/${encodeURIComponent(personID)}/share`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  revokePersonShare: (linkID: string) =>
    request<{ revoked: boolean }>(`/api/v1/person-share-links/${encodeURIComponent(linkID)}`, {
      method: "DELETE",
    }),
  confirmGuestSettlementRequest: (requestID: string) =>
    request<GuestSettlementRequest>(`/api/v1/settlement-requests/${encodeURIComponent(requestID)}/confirm`, {
      method: "POST",
      headers: { "Idempotency-Key": `guest-settlement-confirm-${crypto.randomUUID()}` },
      body: "{}",
    }),
  rejectGuestSettlementRequest: (requestID: string, reason: string) =>
    request<GuestSettlementRequest>(`/api/v1/settlement-requests/${encodeURIComponent(requestID)}/reject`, {
      method: "POST",
      headers: { "Idempotency-Key": `guest-settlement-reject-${crypto.randomUUID()}` },
      body: JSON.stringify({ reason }),
    }),
  getGroupSettlementSuggestions: (groupID: string) =>
    request<SettlementSuggestion[]>(`/api/v1/groups/${encodeURIComponent(groupID)}/settlement-suggestions`),
  previewSplitwiseImport: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<ImportPreview>("/api/v1/imports/splitwise", { method: "POST", body });
  },
  commitImport: (importID: string) =>
    request<ImportPreview>(`/api/v1/imports/${encodeURIComponent(importID)}/commit`, {
      method: "POST",
      body: "{}",
    }),
  deleteImport: (importID: string) =>
    request<void>(`/api/v1/imports/${encodeURIComponent(importID)}`, {
      method: "DELETE",
    }),
  updateMe: (input: ProfileSettingsInput) =>
    request<User>('/api/v1/me', {
      method: 'PATCH',
      body: JSON.stringify({
        email: input.email,
        defaultCurrency: input.currency,
        exchangeRates: input.exchangeRates,
      }),
    }),
  updatePassword: (input: ChangePasswordInput) =>
    request<void>('/api/v1/me/password', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  listLoans: () => request<Loan[]>("/api/v1/loans"),
	getLoanForecast: (loanID: string, payoffDate?: string) => request<LoanForecast>(`/api/v1/loans/${encodeURIComponent(loanID)}/forecast${payoffDate ? `?payoffDate=${encodeURIComponent(payoffDate)}` : ''}`),
	reviewLoanClaim: (claimID: string, confirm: boolean) => request<LoanPaymentClaim>(`/api/v1/loan-claims/${encodeURIComponent(claimID)}/${confirm ? 'confirm' : 'reject'}`, { method: 'POST', body: '{}' }),
	getPersonDebt: (personID: string) => request<PersonDebt>(`/api/v1/people/${encodeURIComponent(personID)}/debt`),
	convertExpenses: (personID: string, terms: CreateLoanInput, expenses: LoanExpenseSource[]) => request<Loan>(`/api/v1/people/${encodeURIComponent(personID)}/convert-expenses`, { method: 'POST', headers: { 'Idempotency-Key': `conversion-${crypto.randomUUID()}` }, body: JSON.stringify({ terms, expenses }) }),
	recordPersonPayment: (personID: string, currency: string, direction: 'paid' | 'received', allocations: PersonPaymentAllocation[]) => request<PersonDebt>(`/api/v1/people/${encodeURIComponent(personID)}/payments`, { method: 'POST', headers: { 'Idempotency-Key': `person-payment-${crypto.randomUUID()}` }, body: JSON.stringify({ currency, direction, allocations }) }),
	updateLoan: (loanID: string, terms: CreateLoanInput) => request<Loan>(`/api/v1/loans/${encodeURIComponent(loanID)}`, { method: 'PATCH', body: JSON.stringify(terms) }),
	adjustLoan: (loanID: string, adjustment: { principalCents: number; interestCents: number; feeCents: number; penaltyCents: number; reason: string }) => request<LoanPayment>(`/api/v1/loans/${encodeURIComponent(loanID)}/adjustments`, { method: 'POST', headers: { 'Idempotency-Key': `adjustment-${crypto.randomUUID()}` }, body: JSON.stringify(adjustment) }),
  createLoan: (input: CreateLoanInput) =>
    request<Loan>("/api/v1/loans", {
      method: "POST",
      headers: { "Idempotency-Key": `loan-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  getLoanBalance: (loanID: string) =>
    request<LoanBalance>(`/api/v1/loans/${loanID}/balance`),
  listLoanPayments: (loanID: string) =>
    request<LoanPayment[]>(`/api/v1/loans/${loanID}/payments`),
  createLoanPayment: (loanID: string, input: CreateLoanPaymentInput) =>
    request<LoanPayment>(`/api/v1/loans/${loanID}/payments`, {
      method: "POST",
      headers: { "Idempotency-Key": `loan-payment-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
};

export function findPerson(
  data: Overview,
  id: string,
): Person | User | undefined {
  if (id === data.currentUser.id) {
    return data.currentUser;
  }
  return data.people.find((person) => person.id === id);
}

export function findGroup(data: Overview, id: string): Group | undefined {
  return data.groups.find((group) => group.id === id);
}

export function formatMoney(amountCents: number, currency: string): string {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

export function centsToInput(amountCents: number): string {
  return (amountCents / 100).toFixed(2);
}

export function parseCents(value: string): number | null {
  const match = value.trim().match(/^(\d+)(?:[.,](\d{1,2}))?$/);
  if (!match) {
    return null;
  }

  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) {
    return null;
  }

  const cents = whole * 100 + fraction;
  return Number.isSafeInteger(cents) ? cents : null;
}

export function formatMinorUnitInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  const cents = Number(digits);
  if (!Number.isSafeInteger(cents)) {
    return "";
  }

  return centsToInput(cents);
}

export function formatPastedMoneyInput(value: string): string | null {
  const cents = parseCents(value);
  return cents === null ? null : centsToInput(cents);
}

export function equalShares(amountCents: number, count: number): number[] {
  if (count < 1) {
    return [];
  }
  const share = Math.floor(amountCents / count);
  const remainder = amountCents % count;
  return Array.from(
    { length: count },
    (_, index) => share + (index < remainder ? 1 : 0),
  );
}

export function localDateInputValue(value = new Date()): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function transactionDateInputValue(value: string, timeSet = false): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (!timeSet) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return localDateInputValue(date);
}

export function transactionTimeInputValue(value: string, timeSet = false): string {
  if (!timeSet) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function combineTransactionDateTime(date: string, time = ""): string {
  if (!time) return `${date}T12:00:00.000Z`;
  const local = new Date(`${date}T${time}:00`);
  return Number.isNaN(local.getTime()) ? `${date}T12:00:00.000Z` : local.toISOString();
}

export function formatTransactionDate(value: string, timeSet = false): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (!timeSet) {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  }
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

export function formatActivityDate(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return formatShortDate(value);
}


export function expenseBalanceDelta(expense: Expense, currentUserID: string, personID: string): number {
  const payerAmounts = expense.payerAmounts ?? { [expense.paidById]: expense.amountCents };
  const participantAmounts = expense.participantAmounts ?? Object.fromEntries(
    expense.participantIds.map((participantID, index) => [
      participantID,
      equalShares(expense.amountCents, expense.participantIds.length)[index] ?? 0,
    ]),
  );
  const net = new Map<string, number>();

  Object.entries(payerAmounts).forEach(([id, amountCents]) => {
    net.set(id, (net.get(id) ?? 0) + amountCents);
  });
  Object.entries(participantAmounts).forEach(([id, amountCents]) => {
    net.set(id, (net.get(id) ?? 0) - amountCents);
  });

  const creditors = [...net.entries()]
    .filter(([, amountCents]) => amountCents > 0)
    .map(([id, amountCents]) => ({ id, amountCents }))
    .sort((left, right) => left.id.localeCompare(right.id));
  const debtors = [...net.entries()]
    .filter(([, amountCents]) => amountCents < 0)
    .map(([id, amountCents]) => ({ id, amountCents: -amountCents }))
    .sort((left, right) => left.id.localeCompare(right.id));

  let creditorIndex = 0;
  let debtorIndex = 0;
  let deltaCents = 0;
  while (creditorIndex < creditors.length && debtorIndex < debtors.length) {
    const creditor = creditors[creditorIndex];
    const debtor = debtors[debtorIndex];
    const amountCents = Math.min(creditor.amountCents, debtor.amountCents);

    if (debtor.id === personID && creditor.id === currentUserID) {
      deltaCents += amountCents;
    } else if (debtor.id === currentUserID && creditor.id === personID) {
      deltaCents -= amountCents;
    }

    creditor.amountCents -= amountCents;
    debtor.amountCents -= amountCents;
    if (creditor.amountCents === 0) creditorIndex += 1;
    if (debtor.amountCents === 0) debtorIndex += 1;
  }

  return deltaCents;
}

export function groupSpent(data: Overview, groupID: string): number {
  return data.expenses
    .filter((expense) => expense.groupId === groupID)
    .reduce((total, expense) => total + expense.amountCents, 0);
}

export function groupBalanceByPerson(
  data: Overview,
  groupID: string,
): Map<string, number> {
  const balances = new Map<string, number>();
  const expenses = data.expenses.filter(
    (expense) => expense.groupId === groupID,
  );

  expenses.forEach((expense) => {
    const shares = equalShares(
      expense.amountCents,
      expense.participantIds.length,
    );
    expense.participantIds.forEach((participantID, index) => {
      const share = shares[index];
      if (
        expense.paidById === data.currentUser.id &&
        participantID !== data.currentUser.id
      ) {
        balances.set(participantID, (balances.get(participantID) ?? 0) + share);
      }
      if (
        participantID === data.currentUser.id &&
        expense.paidById !== data.currentUser.id
      ) {
        balances.set(
          expense.paidById,
          (balances.get(expense.paidById) ?? 0) - share,
        );
      }
    });
  });

  return balances;
}
export const guestShareAPI = {
  get: (token: string) =>
    request<GuestShareView>(`/api/share/${encodeURIComponent(token)}`),
  requestSettlement: (token: string, amountCents: number, note: string) =>
    request<GuestShareView>(`/api/share/${encodeURIComponent(token)}/settlement-requests`, {
      method: "POST",
      headers: { "Idempotency-Key": `guest-settlement-request-${crypto.randomUUID()}` },
      body: JSON.stringify({ amountCents, note }),
    }),
};

export const personShareAPI = {
	forecast: (token: string, loanID: string, payoffDate?: string) => request<LoanForecast>(`/api/person-share/${encodeURIComponent(token)}/loans/${encodeURIComponent(loanID)}/forecast${payoffDate ? `?payoffDate=${encodeURIComponent(payoffDate)}` : ''}`),
	claimLoanPayment: (token: string, loanId: string, amountCents: number, note: string) => request<PersonShareView>(`/api/person-share/${encodeURIComponent(token)}/loan-claims`, { method: 'POST', headers: { 'Idempotency-Key': `loan-claim-${crypto.randomUUID()}` }, body: JSON.stringify({ loanId, amountCents, note }) }),
	acknowledge: (token: string) => request<PersonShareView>(`/api/person-share/${encodeURIComponent(token)}/acknowledge`, { method: 'POST', body: '{}' }),
  get: (token: string) =>
    request<PersonShareView>(`/api/person-share/${encodeURIComponent(token)}`),
  requestSettlement: (token: string, currency: string, amountCents: number, note: string) =>
    request<PersonShareView>(`/api/person-share/${encodeURIComponent(token)}/settlement-requests`, {
      method: "POST",
      headers: { "Idempotency-Key": `person-settlement-request-${crypto.randomUUID()}` },
      body: JSON.stringify({ currency, amountCents, note }),
    }),
};
